
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { useEffect } from 'react';

export type FavoriteArtistType = 'artist' | 'mock' | 'shop';

interface FavoriteArtist {
  id: string;
  user_id: string;
  artist_id: string;
  artist_type: FavoriteArtistType;
  created_at: string;
}

interface FavoriteArtistsState {
  favorites: FavoriteArtist[];
  loading: boolean;
  fetchFavorites: (userId: string) => Promise<void>;
  addFavorite: (userId: string, artistId: string, artistType: FavoriteArtistType) => Promise<void>;
  removeFavorite: (userId: string, artistId: string, artistType: FavoriteArtistType) => Promise<void>;
  isFavorite: (artistId: string, artistType: FavoriteArtistType) => boolean;
  clearFavorites: () => void;
}

/**
 * Zustand store handling favorite artists.
 * Includes robust error handling and ensures state consistency.
 */
const useFavoriteArtistsStore = create<FavoriteArtistsState>((set, get) => ({
  favorites: [],
  loading: false,

  /** Fetch all favorites for a given user */
  fetchFavorites: async (userId: string) => {
    set({ loading: true });
    try {
      const { data, error } = await supabase
        .from('favorite_artists')
        .select('*')
        .eq('user_id', userId);

      if (error) throw error;
      // Ensure we always store an array
      set({ favorites: Array.isArray(data) ? data : [] });
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      set({ loading: false });
    }
  },

  /** Add a new favorite */
  addFavorite: async (userId: string, artistId: string, artistType: FavoriteArtistType) => {
    try {
      const { data, error } = await supabase
        .from('favorite_artists')
        .insert([{ user_id: userId, artist_id: artistId, artist_type: artistType }])
        .select()
        .maybeSingle();

      if (error) throw error;
      if (data) {
        set((state) => ({
          favorites: [...state.favorites, data as FavoriteArtist],
        }));
      }
    } catch (error) {
      console.error('Error adding favorite:', error);
    }
  },

  /** Remove an existing favorite */
  removeFavorite: async (userId: string, artistId: string, artistType: FavoriteArtistType) => {
    try {
      const { error } = await supabase
        .from('favorite_artists')
        .delete()
        .eq('user_id', userId)
        .eq('artist_id', artistId)
        .eq('artist_type', artistType);

      if (error) throw error;
      set((state) => ({
        favorites: state.favorites.filter(
          (f) => !(f.artist_id === artistId && f.artist_type === artistType)
        ),
      }));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  },

  /** Check if an artist is already favorited */
  isFavorite: (artistId: string, artistType: FavoriteArtistType) => {
    const { favorites } = get();
    return favorites.some(
      (f) => f.artist_id === artistId && f.artist_type === artistType
    );
  },

  /** Clear all favorites (used on logout) */
  clearFavorites: () => {
    set({ favorites: [] });
  },
}));

/**
 * Hook exposing the store plus a convenient toggle method.
 */
export function useFavoriteArtists() {
  const { user } = useAuth();
  const store = useFavoriteArtistsStore();

  useEffect(() => {
    if (user) {
      store.fetchFavorites(user.id);
    } else {
      store.clearFavorites();
    }
    // Dependency on user?.id ensures re‑run when the auth state changes
  }, [user?.id]);

  return {
    ...store,
    /** Toggle favorite status for a given artist */
    toggleFavorite: async (artistId: string, artistType: FavoriteArtistType) => {
      if (!user) return;

      if (store.isFavorite(artistId, artistType)) {
        await store.removeFavorite(user.id, artistId, artistType);
      } else {
        await store.addFavorite(user.id, artistId, artistType);
      }
    },
  };
}
